import React, {memo, useEffect, useState} from 'react';

import {ButtonReact} from 'common/components/button';
import {DropMenuReact} from 'common/components/drop-menu';
import {useTranslation} from 'common/i18n/hooks';
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';
import {
  EntrySectionUpdates,
  RowLabelTheme,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {getProjectId} from 'common/report/util';
import {SelectReact} from 'common/components/select';
import {ResourceType} from 'common/report/constants';
import {Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {formatMetricNumberWithUnits} from 'common/util';
import {ButtonTheme} from 'common/components/button/constants';

import {DateRange} from '../date-range-picker/models/date-range';
import {DateRangePickerReact} from '../date-range-picker-new';
import {DisplayContext} from '../date-range-picker/models/types';
import styles from './index.module.styl';
import {FilterEntry, RecordingPlayerSource, SortOption, SortOptionTranslationLabels} from './types';
import {getSortOption, isSortOptionAscending} from './util';
import {REPLAY_EVENT} from './query-utils';

type RecordingPlaylistHeaderProps = {
  source: RecordingPlayerSource;
  selectedSortOption: SortOption;
  updateSortOptionFn: (option: SortOption) => void;
  visibleRecordingsCount: number;
  // Optional props for SR Home
  loadingReplays?: boolean;
  replayCount?: number;
  replayDateRange?: DateRange;
  setDateRangeFn?: (dateRange: DateRange) => void;
  eventFilterEntries?: FilterEntry[];
  setEventFilterEntries?: (entries: FilterEntry[]) => void;
  recordingPropertyFilters?: FilterEntry[];
  setRecordingPropertyFilters?: (entries: FilterEntry[]) => void;
};

const SORT_OPTIONS_VALUES = [SortOption.MostRecent, SortOption.LongestDuration, SortOption.MostEvents];
function PlaylistHeaderComponent(props: RecordingPlaylistHeaderProps) {
  const [dateMenuOpened, setDateMenuOpened] = useState<boolean>(false);
  const [eventFilterMenuOpened, setEventFilterMenuOpened] = useState<boolean>(false);
  const [numFiltersActive, setNumFiltersActive] = useState<number>(0);
  const [isSortAscending, setSortAscending] = useState<boolean>(isSortOptionAscending(props.selectedSortOption));

  const [currentEventEntries, setCurrentEventEntries] = useState<FilterEntry[]>(props.eventFilterEntries ?? []);
  const [currentRecordingPropEntries, setCurrentRecordingPropEntries] = useState<FilterEntry[]>(
    props.recordingPropertyFilters ?? [],
  );
  const [isPropClicked, setIsPropClicked] = useState<boolean>(false);

  useEffect(() => {
    setCurrentEventEntries(props.eventFilterEntries ?? []);
  }, [props.eventFilterEntries]);

  useEffect(() => {
    setCurrentRecordingPropEntries(props.recordingPropertyFilters ?? []);
  }, [props.recordingPropertyFilters]);

  const {t, print} = useTranslation();
  const projectId = getProjectId();

  const resetFilters = () => {
    setCurrentEventEntries(props.eventFilterEntries ?? []);
    setCurrentRecordingPropEntries(props.recordingPropertyFilters ?? []);
    setIsPropClicked(false);
  };

  function getReplayCountDisplayText(): string {
    if (props.loadingReplays) {
      return t(`session-replay.loading-replays`);
    }

    if (props.replayCount === undefined || props.replayCount === null) {
      return t(`session-replay.replays-count-subtotal-loading`, {
        subtotal: props.visibleRecordingsCount,
      });
    }

    if (props.replayCount >= 0) {
      return t(`session-replay.replays-count-subtotal`, {
        subtotal: props.visibleRecordingsCount,
        total: formatMetricNumberWithUnits(props.replayCount, {}, {abbreviateThousands: true}),
      });
    }

    return t(`session-replay.replays-count-fail`, {
      number: props.visibleRecordingsCount.toLocaleString(),
    });
  }

  return (
    <div className={styles.replaysListHeader}>
      {props.source === RecordingPlayerSource.ReplayHome ? (
        <div className={styles.replaysListHeaderFilter}>
          <ButtonReact
            disabled={props.loadingReplays}
            className={styles.replayDateButton}
            onClick={() => {
              setDateMenuOpened(true);
              setEventFilterMenuOpened(false);
            }}
            icon='calendar'
          >
            {props.replayDateRange ? print(props.replayDateRange.getDisplayText()) : t(`session-replay.unknown-date`)}
          </ButtonReact>
          <DropMenuReact
            open={dateMenuOpened}
            disableAutoFocus
            // @ts-ignore this onChange type is conflicting with dom onChange form event
            onChange={(ev: VisibilityChangeEvent) => {
              if (ev.detail.state !== Visibility.Open) {
                setDateMenuOpened(false);
              }
            }}
          >
            <DateRangePickerReact
              forceHidePresetMenu
              displayContext={DisplayContext.FullLength}
              onApply={(ev) => {
                if (props.setDateRangeFn) {
                  props.setDateRangeFn(ev.detail.dateRange);
                  setDateMenuOpened(false);
                }
              }}
              onCancel={() => setDateMenuOpened(false)}
            />
          </DropMenuReact>
          <div className={styles.organizationContainer}>
            <DropMenuReact
              preventStacking={currentEventEntries !== props.eventFilterEntries || isPropClicked}
              className={styles.filterContainer}
              open={eventFilterMenuOpened}
              // @ts-ignore this onChange type is conflicting with dom onChange form event
              onChange={(ev: VisibilityChangeEvent) => {
                if (ev.detail.state !== Visibility.Open) {
                  setEventFilterMenuOpened(false);
                  resetFilters();
                }
              }}
            >
              <div className={styles.filterMenu}>
                <div className={styles.sectionTitle}>{t(`session-replay.event-filter`)}</div>
                <div className={styles.sectionSubtitle}>{t(`session-replay.event-filter-desc`)}</div>
                <QueryEntrySectionReact
                  className={styles.filterBuilder}
                  rowTypes={[ResourceType.Event]}
                  rowLabelTheme={RowLabelTheme.SessionReplayLabel}
                  projectId={+projectId}
                  entries={currentEventEntries}
                  disableCloneEntry
                  onUpdate={(updates: EntrySectionUpdates<any>) => {
                    setCurrentEventEntries(updates.entries);
                  }}
                />
                <div className={styles.sectionTitle}>{t(`session-replay.property-filter`)}</div>
                <div className={styles.sectionSubtitle}>{t(`session-replay.property-filter-desc`)}</div>
                <QueryEntrySectionReact
                  className={styles.filterBuilder}
                  onClick={() => {
                    setIsPropClicked(true);
                  }}
                  rowLabelType={RowLabelType.None}
                  rowTypes={[SelectorTypeDefinitions.Filter]}
                  projectId={+projectId}
                  entries={currentRecordingPropEntries}
                  disableCloneEntry
                  showFilterPrefixForFilterType
                  eventNames={[REPLAY_EVENT]}
                  onUpdate={(updates: EntrySectionUpdates<any>) => {
                    setCurrentRecordingPropEntries(updates.entries);
                  }}
                />
                <div className={styles.completionContainer}>
                  <ButtonReact
                    className={styles.filterSelectButton}
                    theme={ButtonTheme.Tertiary}
                    onClick={() => {
                      setEventFilterMenuOpened(false);
                      resetFilters();
                    }}
                  >
                    {t(`common.cancel`)}
                  </ButtonReact>
                  <ButtonReact
                    className={styles.filterSelectButton}
                    theme={ButtonTheme.Primary}
                    onClick={() => {
                      let totalNumFilters = currentRecordingPropEntries.length;
                      currentEventEntries.forEach((entry) => {
                        // Add one for the event filter itself
                        totalNumFilters += entry.filters.length + 1;
                      });
                      setNumFiltersActive(totalNumFilters);
                      setEventFilterMenuOpened(false);
                      if (props.setEventFilterEntries) {
                        props.setEventFilterEntries(currentEventEntries);
                      }
                      if (props.setRecordingPropertyFilters) {
                        props.setRecordingPropertyFilters(currentRecordingPropEntries);
                      }
                      setEventFilterMenuOpened(false);
                    }}
                  >
                    {t(`common.apply`)}
                  </ButtonReact>
                </div>
              </div>
            </DropMenuReact>

            <ButtonReact
              disabled={props.loadingReplays}
              className={styles.filterButton}
              onClick={() => {
                setDateMenuOpened(false);
                setEventFilterMenuOpened(true);
              }}
              icon='filter-xs'
            >
              {numFiltersActive > 0
                ? t(`session-replay.num-filters-set`, {count: numFiltersActive})
                : t(`query-builder.filter`)}
            </ButtonReact>
            <div className={styles.sortButton}>
              <SelectReact
                disabled={props.loadingReplays}
                className={styles.optionSelect}
                menuWidth={-1}
                sections={[
                  {
                    items: SORT_OPTIONS_VALUES.map((option) => ({
                      value: option,
                      label: print(t(SortOptionTranslationLabels[option])),
                    })),
                  },
                ]}
                options={SORT_OPTIONS_VALUES}
                onChange={(ev: any) => {
                  const selectedOption = ev.detail.item.value as SortOption;
                  const newOption = getSortOption(selectedOption, isSortAscending);
                  props.updateSortOptionFn(newOption);
                }}
                selectedOption={print(t(SortOptionTranslationLabels[props.selectedSortOption]))}
                tooltip={{text: t(`session-replay.sort-tooltip`)}}
              />
              <ButtonReact
                className={styles.ascendingSelect}
                icon={isSortAscending ? `sort-value-asc` : `sort-value-desc`}
                disabled={props.loadingReplays}
                onClick={() => {
                  setSortAscending(!isSortAscending);
                  const newOption = getSortOption(props.selectedSortOption, !isSortAscending);
                  props.updateSortOptionFn(newOption);
                }}
              />
            </div>
          </div>
          <div className={styles.replaysListHeaderSubtitle}>{getReplayCountDisplayText()}</div>
        </div>
      ) : (
        <div className={styles.replaysListHeaderText}>
          <div className={styles.replaysListHeaderTitle}>{t(`session-replay.replays`)}</div>
          {!props.loadingReplays && (
            <div className={styles.replaysListHeaderSubtitle}>
              {t(`session-replay.replays-count`, {number: props.visibleRecordingsCount})}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Reduce the amount of time this component renders since it exists in the player.
export const PlaylistHeader = memo(PlaylistHeaderComponent);
